import { useState, type MouseEvent, type ReactNode } from 'react';
import { Menu, MenuItem, Divider } from '@mui/material';
import { useAIActions } from '../../api/queries/aiActions';
import { AIActionDialog } from './AIActionDialog';

interface AIContextMenuProps {
  children: ReactNode;
  contextParams?: Record<string, unknown>;
}

/**
 * Right-click context menu on any child. Lists all registry actions and opens an
 * AIActionDialog when one is picked.
 */
export function AIContextMenu({ children, contextParams }: AIContextMenuProps) {
  const { data: actions = [] } = useAIActions();
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null);
  const [dialogSlug, setDialogSlug] = useState<string | null>(null);

  const onContextMenu = (e: MouseEvent) => {
    e.preventDefault();
    setAnchor({ x: e.clientX, y: e.clientY });
  };

  const selected = actions.find((a) => a.slug === dialogSlug) ?? null;

  return (
    <>
      <div onContextMenu={onContextMenu}>{children}</div>
      <Menu
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorReference="anchorPosition"
        anchorPosition={anchor ? { top: anchor.y, left: anchor.x } : undefined}
      >
        {actions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => {
              setAnchor(null);
              setDialogSlug(action.slug);
            }}
          >
            {action.name}
          </MenuItem>
        ))}
        {actions.length === 0 && (
          <MenuItem disabled>No actions available</MenuItem>
        )}
        <Divider />
        <MenuItem disabled>
          {'⌘/'} inside the editor for slash commands
        </MenuItem>
      </Menu>

      <AIActionDialog
        open={selected !== null}
        onClose={() => setDialogSlug(null)}
        action={selected}
        contextParams={contextParams}
      />
    </>
  );
}
